/**
 * NavRail — left navigation for the Creative OS Home cockpit. Every entry is
 * a real route (react-router NavLink); collapsible. Presentation only.
 */
import { useEffect, useRef, useState, type CSSProperties } from "react";
import { NavLink } from "react-router";
import { motion } from "motion/react";
import { FocusScope } from "@react-aria/focus";
import { KitBrandPicker } from "./kit/BrandPicker";
import { isDevMode } from "@/config/envvars";
import { useWorkspaceStore } from "@/stores/workspaceStore";
import { useSocialConnections } from "@/app/routes/dashboard/settings/api/useSocialConnections";
import NavAiBudgetBar from "./NavAiBudgetBar";
import { useNavRailTip } from "./NavRailTip";
import ProfileMenu from "./ProfileMenu";
import NotificationMenu from "./NotificationMenu";
import { usePermissions } from "@/hooks/usePermissions";
import { GUEST_HIDDEN_NAV } from "../guestAccess";
import { NavGlyph, type NavGlyphName } from "./NavGlyph";

type Item = { to: string; label: string; glyph: NavGlyphName; end?: boolean };
type Group = { label: string | null; items: Item[] };

const GROUPS: Group[] = [
  {
    label: null,
    items: [{ to: ".", end: true, label: "Home", glyph: "home" }],
  },
  {
    label: "Brand",
    items: [
      { to: "brand-universe", label: "Universe", glyph: "universe" },
      { to: "strategy", label: "Strategy", glyph: "strategy" },
    ],
  },
  {
    label: "Intelligence",
    items: [
      { to: "market-signals", label: "Signals", glyph: "signals" },
      { to: "trends", label: "Trends", glyph: "trends" },
      { to: "references", label: "References", glyph: "references" },
    ],
  },
  {
    label: "Create",
    items: [
      { to: "workshop", label: "Workshop", glyph: "workshop" },
      { to: "content-calendar", label: "Calendar", glyph: "calendar" },
    ],
  },
];

/** Dev-only pages. Whole group is gated on isDevMode, not per-item.
 *  Media is a product surface we'll open to everyone later; until then it
 *  lives here so there's no production entry point. */
const DEV_GROUP: Group = {
  label: "Dev Tools",
  items: [
    { to: "agent-runs", label: "Agent Runs", glyph: "agent-runs" },
    { to: "brand-media", label: "Media", glyph: "media" },
  ],
};

/* One geometry for both rail states, so the fold never moves an icon: the
   content column is 71 (72 less the clear right stroke) and every glyph is
   centred on 35.5, at the same y open or folded. The open rail is the folded
   one with text to the right of each icon. */

/** Air around the desktop rail so it reads as an island, not a docked column.
 *  The rail itself stays 72 / 236; the slot grows by this gap on both sides. */
const ISLAND_GAP = 12;

/** A footer control. The 18 glyph stays at 6 inside a row that starts at 20.5,
 *  folded or open. Opening only lets the row run to the right so the label
 *  can appear; the glyph's box does not change. */
function footRowStyle(expanded: boolean): React.CSSProperties {
  return {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-start",
    gap: 12,
    padding: "0 6px",
    width: expanded ? "100%" : 30,
    height: 30,
    flex: "none",
    cursor: "pointer",
    fontFamily: "var(--font-body)",
    fontWeight: 600,
    fontSize: 13,
    letterSpacing: 0,
    textAlign: "left",
  };
}

type Props = {
  /** Inline: rail is expanded (labels) vs collapsed (icons). Overlay: drawer is on screen. */
  open: boolean;
  /** Below the shell breakpoint the rail leaves the flex flow and slides over the
      page as a drawer, so opening it never shifts content. Always expanded. */
  overlay?: boolean;
  /** Overlay only — dismiss the drawer after a destination is picked. */
  onNavigate?: () => void;
  /** Pending-review signal count; drives the live count / dot on the Signals row. */
  signalsPending?: number;
  /** Incoming org invites; drives the count / dot on Settings. */
  invitesPending?: number;
  theme: "light" | "dark";
  onToggleTheme: () => void;
  /** Fold or unfold the rail; in the overlay drawer, the menu button that
   *  closes it (Figma 11 · Shell, the button right of the brand row). */
  onToggle?: () => void;
  /** Opens the global bug / recommendation modal. */
  onOpenFeedback?: () => void;
  /** Hide brand picker when the user has no organization. */
  hideBrandPicker?: boolean;
};

export default function NavRail({ open, overlay, onNavigate, signalsPending, invitesPending, theme, onToggleTheme, onToggle, onOpenFeedback, hideBrandPicker }: Props) {
  const invites = invitesPending ?? 0;
  const { selectedBrandId } = useWorkspaceStore();
  const { isGuest } = usePermissions();
  // A guest connects their brand's accounts too, so their rail asks to reconnect.
  const connectionsQuery = useSocialConnections(selectedBrandId);
  const needsReauth = (connectionsQuery.data ?? []).some((c) => c.status === "NEEDS_REAUTH");
  const reconnectTip = "Reconnect a social account";
  /* M6 · the rail opens and folds (Figma 11): the rail grows from 72 to 236
     and the page moves with it (layout). Labels and group heads fade in and
     rise 4px, 20ms apart, from +120ms. Folding: the labels leave first (100ms),
     then the rail closes (220ms ease-in). Reduced motion: the width changes at
     once and the labels cross-fade. */
  const [deskExpanded, setDeskExpanded] = useState(open);
  const [folding, setFolding] = useState(false);
  const [revealing, setRevealing] = useState(false);
  const [slotWide, setSlotWide] = useState(open);
  const firstRun = useRef(true);
  useEffect(() => {
    if (overlay) return;
    // The remembered state paints at once, without playing the open.
    if (firstRun.current) {
      firstRun.current = false;
      return;
    }
    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (open) {
      setFolding(false);
      setDeskExpanded(true);
      setSlotWide(true);
      setRevealing(true);
      const id = window.setTimeout(() => setRevealing(false), reduced ? 200 : 600);
      return () => window.clearTimeout(id);
    }
    setRevealing(false);
    setFolding(true);
    const narrow = window.setTimeout(() => setSlotWide(false), reduced ? 0 : 100);
    const done = window.setTimeout(() => {
      setDeskExpanded(false);
      setFolding(false);
    }, reduced ? 160 : 320);
    return () => {
      window.clearTimeout(narrow);
      window.clearTimeout(done);
    };
  }, [open, overlay]);
  // A drawer is always expanded — collapsing to an icon strip only makes sense
  // when the rail is permanently on screen.
  const expanded = overlay ? true : deskExpanded;
  const { bindTip, tipPortal } = useNavRailTip(!expanded);
  // The reveal stagger: each group head and item name gets its position in the
  // list, computed from the groups themselves so it never depends on render order.
  const navGroups = (isGuest
    ? GROUPS.map((g) => ({ ...g, items: g.items.filter((it) => !GUEST_HIDDEN_NAV.has(it.to)) })).filter(
        (g) => g.items.length > 0,
      )
    : isDevMode
      ? [...GROUPS, DEV_GROUP]
      : GROUPS
  ).map((g, gi, all) => {
    const before = all.slice(0, gi).reduce((n, p) => n + (p.label ? 1 : 0) + p.items.length, 0);
    return { ...g, headIndex: before, itemStart: before + (g.label ? 1 : 0) };
  });
  const stagger = (i: number): CSSProperties => ({ "--i": i }) as CSSProperties;

  /* M3 · the drawer (Figma 11): in on smooth, out in 220ms ease-in; the rail
     keeps its labels. 236 wide, as the expanded rail. */
  const overlayStyle: React.CSSProperties = {
    position: "fixed",
    top: 0,
    bottom: 0,
    left: 0,
    width: 236,
    maxWidth: "82vw",
    // Same layer as the scrim; the rail wins on DOM order (it renders after).
    zIndex: "var(--z-index-drawer)",
    transform: open ? "translateX(0)" : "translateX(-100%)",
    // Deferred so the panel stays painted through the close animation, then
    // drops out of the tab order instead of lurking off-screen.
    visibility: open ? "visible" : "hidden",
    transition: open
      ? "transform var(--spring-smooth), visibility 0s"
      : "transform 220ms cubic-bezier(0.4, 0, 1, 1), visibility 0s 220ms",
    boxShadow: open ? "var(--co-chrome-rail-shadow)" : undefined,
  };

  const deskStyle: CSSProperties = {
    position: "absolute",
    top: ISLAND_GAP,
    right: ISLAND_GAP,
    bottom: ISLAND_GAP,
    left: ISLAND_GAP,
  };

  const rail = (
      <aside
        id="co-nav-rail"
        className={overlay ? "co-rail" : "co-rail co-nav-island"}
        data-expanded={expanded ? "true" : undefined}
        data-revealing={!overlay && revealing ? "true" : undefined}
        data-folding={!overlay && folding ? "true" : undefined}
        role={overlay && open ? "dialog" : undefined}
        aria-modal={overlay && open ? true : undefined}
        aria-label={overlay && open ? "Navigation" : undefined}
        style={{
          /* The chassis edge is declared on #co-nav-rail in cockpit.css: in light
             the rail, the header and a card are all #ffffff, so the hairline is
             the whole separation and belongs to the shell, not to this file. */
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          // The docked drawer keeps a 1px clear right stroke so its column is 71.
          // On the island that stroke opens a seam between the fill and the radius.
          ...(overlay ? { borderRight: "1px solid transparent", ...overlayStyle } : deskStyle),
        }}
      >
      {/* Header stays two rows in both rail states: the menu on the first,
          the brand tile on the second, both pinned to the folded x. Opening
          only reveals the brand name to the right of that same tile. */}
      <div style={{ padding: "18px 0 14px", borderBottom: "1px solid var(--co-line)", display: "flex", flexDirection: "column", gap: 8 }}>
        <div style={{ display: "flex", alignItems: "center", height: 30, padding: "0 18px 0 20.5px" }}>
          {onToggle ? (
            <button
              type="button"
              className="co-icon-ghost"
              onClick={onToggle}
              aria-label={overlay ? "Close navigation" : expanded ? "Collapse the menu" : "Expand the menu"}
              aria-expanded={expanded}
              aria-controls="co-nav-rail"
              style={{ width: 30, height: 30, display: "grid", placeItems: "center", cursor: "pointer", flex: "none" }}
            >
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                <path d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            </button>
          ) : null}
        </div>
        {hideBrandPicker ? (
          expanded ? (
            <span className="co-rail-text" style={{ paddingLeft: 19.5, fontFamily: "var(--font-section)", fontWeight: 800, fontSize: 15, letterSpacing: "-.01em", whiteSpace: "nowrap" }}>Creo</span>
          ) : null
        ) : (
          <div style={{ display: "flex", alignItems: "center", height: 36, paddingLeft: 19.5, paddingRight: expanded ? 12 : 0 }}>
            {/* 32 wide when folded, so the tile's mx-auto cannot drift as the rail grows. */}
            <div style={{ width: expanded ? "100%" : 32, minWidth: 0 }}>
              <KitBrandPicker variant="header" compact={!expanded} folded={!overlay && !open} />
            </div>
          </div>
        )}
      </div>

      {/* One list geometry both ways. Pad 8/7.5/12, gap 4, rows 40. A folded
          item is the 56 pill; open, that same pill grows right and the name
          fades in. The glyph stays at 26.5 (7.5 + 19). */}
      <motion.nav
        layout="position"
        transition={{ layout: { duration: 0.22, ease: [0.4, 0, 0.2, 1] } }}
        className="no-scrollbar"
        aria-label="Main navigation" style={{ flex: 1, padding: overlay ? "8px 7.5px 12px" : "8px 8px 12px", display: "flex", flexDirection: "column", gap: 4, overflowY: "auto", position: "relative", zIndex: 1 }}>
        {navGroups.map((g, gi) => (
          <div
            key={g.label ?? `g${gi}`}
            role={g.label ? "group" : undefined}
            aria-label={g.label ?? undefined}
            style={{ display: "flex", flexDirection: "column", gap: 4 }}
          >
            {/* The head is 23 either way, so the rows under it never shift.
                Open, the name replaces the rule in that same box. */}
            {g.label ? (
              <div style={{ height: 23, display: "flex", alignItems: "center", paddingLeft: expanded ? 19 : 4, paddingRight: 4, flex: "none" }}>
                {expanded ? (
                  <div className="co-rail-text" style={{ ...stagger(g.headIndex), fontFamily: "var(--font-body)", fontWeight: 600, fontSize: 10, lineHeight: "14px", letterSpacing: "1.8px", textTransform: "uppercase", color: "var(--co-faint)", whiteSpace: "nowrap" }}>{g.label}</div>
                ) : (
                  <div aria-hidden="true" style={{ height: 1, width: "100%", background: "var(--co-line)" }} />
                )}
              </div>
            ) : null}
            {g.items.map((it, ii) => {
              // The Signals row carries a live pending-review count: an accent
              // count when the rail is open, a 5px accent dot when it is collapsed.
              const pending = it.to === "market-signals" ? (signalsPending ?? 0) : 0;
              const navLabel = pending > 0 ? `${it.label}, ${pending} pending review` : it.label;
              /* Nav item (library, style B, Emad 1 Oct option 2): icons only on
                 the collapsed rail, a name in a tooltip on hover; the selected
                 item sits on a grey pill with its glyph in the accent. The
                 expanded rail keeps the names. */
              return (
                <NavLink
                  key={it.to}
                  to={it.to}
                  end={it.end}
                  className="co-nav unstyled-link"
                  aria-label={navLabel}
                  onClick={onNavigate}
                  {...bindTip(it.label)}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "flex-start",
                    gap: 12,
                    height: 40,
                    // 19 centres the 18 glyph in the folded 56. Open keeps it.
                    paddingLeft: 19,
                    paddingRight: 12,
                    cursor: "pointer",
                    fontFamily: "var(--font-body)",
                    fontWeight: 600,
                    fontSize: 13,
                    letterSpacing: 0,
                    textAlign: "left",
                    width: expanded ? "100%" : 56,
                    flex: "none",
                  }}
                >
                  {({ isActive }) => (
                    <span data-active={String(isActive)} aria-current={isActive ? "page" : undefined} style={{ display: "contents" }}>
                      <NavGlyph name={it.glyph} active={isActive} />
                      {expanded ? (
                        <span className="co-nav-label" style={{ ...stagger(g.itemStart + ii), whiteSpace: "nowrap", flex: 1, overflow: "hidden", textOverflow: "ellipsis" }}>{it.label}</span>
                      ) : null}
                      {expanded && pending > 0 ? <span className="co-rail-text" style={{ fontFamily: "var(--font-mono)", fontWeight: 400, fontSize: 10, letterSpacing: "1px", color: "var(--co-accent)", flex: "none" }}>{pending > 9 ? "9+" : String(pending)}</span> : null}
                      {!expanded && pending > 0 ? <span aria-hidden="true" style={{ position: "absolute", top: 8, left: 36, width: 5, height: 5, borderRadius: "50%", background: "var(--co-accent)" }} /> : null}
                    </span>
                  )}
                </NavLink>
              );
            })}
          </div>
        ))}
      </motion.nav>

      {/* Footer. 20.5 inset both ways, so a 30 row's glyph stays on 35.5.
          Open rows grow right inside that inset. */}
      <div
        style={{
          padding: "10px 20.5px 14px",
          borderTop: "1px solid var(--co-line)",
          display: "flex",
          flexDirection: "column",
          gap: 6,
          alignItems: "stretch",
        }}
      >
        {needsReauth ? (
          <NavLink
            to={{ pathname: "settings", search: "?page=connections" }}
            className="co-nav unstyled-link"
            aria-label={reconnectTip}
            onClick={onNavigate}
            {...bindTip(reconnectTip)}
            style={{ ...footRowStyle(expanded), color: "var(--co-attention, #c2410c)" }}
          >
            {/* An 18px mark, the same size as the rail's other glyphs; the
                tinted box used to fill the whole 30px slot. */}
            <span
              aria-hidden="true"
              style={{
                flex: "none",
                width: 18,
                height: 18,
                display: "grid",
                placeItems: "center",
                borderRadius: 999,
                border: "1.5px solid var(--co-alert-ink)",
                background: "color-mix(in srgb, var(--co-alert-ink) 10%, transparent)",
                fontSize: 11,
                fontWeight: 700,
                lineHeight: 1,
              }}
            >
              !
            </span>
            {expanded ? <span className="co-rail-text" style={{ flex: 1, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>Reconnect account</span> : null}
          </NavLink>
        ) : null}
        <NotificationMenu expanded={expanded} onNavigate={onNavigate} bindTip={bindTip} />
        <NavAiBudgetBar open={expanded} />
        {/* The person ends the rail: Settings, Feedback and Appearance live
            in the profile menu (library 01, Nav / Profile menu). */}
        <ProfileMenu
          compact={!expanded}
          invitesPending={invites}
          onNavigate={onNavigate}
          theme={theme}
          onToggleTheme={onToggleTheme}
          onOpenFeedback={onOpenFeedback}
        />
      </div>
      {tipPortal}
      </aside>
  );

  return (
    <FocusScope contain={overlay && open}>
      {overlay ? (
        rail
      ) : (
        <div
          style={{
            position: "relative",
            width: (slotWide ? 236 : 72) + ISLAND_GAP * 2,
            flex: "none",
            zIndex: "var(--z-index-drawer)",
            transition: slotWide ? "width var(--spring-smooth)" : "width 220ms cubic-bezier(0.4, 0, 1, 1)",
          }}
        >
          {rail}
        </div>
      )}
    </FocusScope>
  );
}
